import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, AlertTriangle, FlaskConical, Ban, Search, ChevronRight, ChevronDown, Wrench, UserCheck, Cog } from 'lucide-react';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/auth';
import { Pill } from '../../components/TwDataTable';
import { Select, Input } from '../../ui';
import { formatIst } from '../../lib/time';
import { warehouseLabel } from '../../lib/warehouseLabel';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qf = (n: any) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const dateOnly = (v: any) => (v ? String(v).substring(0, 10) : null);
const dt = (v: any) => (v ? formatIst(v) : null);
// Whole days since a (date-only) value — positive = in the past. Used for "expired N days ago".
const daysSince = (v: any) => { if (!v) return null; const d = Math.floor((Date.now() - new Date(`${String(v).substring(0, 10)}T00:00:00`).getTime()) / 86400000); return Number.isFinite(d) ? d : null; };

// Why a batch can't be sold → label + pill colour.
const REASON_META: Record<string, { label: string; color: string }> = {
  EXPIRED: { label: 'Expired', color: 'red' },
  QC_HOLD: { label: 'QC hold', color: 'orange' },
  BLOCKED: { label: 'Blocked', color: 'purple' },
};

// The original return condition (as classified on receipt) → a human phrase for the provenance line.
const CONDITION_LABEL: Record<string, string> = {
  DAMAGED: 'damaged', DEFECTIVE: 'defective', MISMATCH: 'batch mismatch', EXPIRED: 'expired', BLOCKED: 'blocked', NEAR_EXPIRY: 'near-expiry', RESALABLE: 'resalable',
};

// Per-reason plain-language explanation + what to do about it, shown in the expanded detail.
function reasonDetail(r: any): { what: string; action: string } {
  if (r.reason === 'EXPIRED') {
    const d = daysSince(r.expDate);
    const what = r.expDate
      ? (d != null && d > 0 ? `Past its expiry date — expired ${d} day${d === 1 ? '' : 's'} ago.` : 'Past its expiry date.')
      : 'Marked expired manually (e.g. an expired return booked in by the warehouse).';
    return { what, action: 'Cannot be sold or dispatched — write off / dispose of this batch.' };
  }
  if (r.reason === 'QC_HOLD') return { what: 'Held for quality inspection — flagged damaged, defective, or mismatched on receipt or return.', action: 'Inspect, then reclassify: restock if good, or write off / return to vendor.' };
  return { what: 'Administratively blocked — recall, regulatory hold, or under investigation.', action: 'Awaiting release; returns to sellable stock once unblocked.' };
}

/**
 * Non-sellable / quarantine stock register — every on-hand batch that can't be sold (expired, QC-hold, blocked,
 * or past its expiry date). Reason-categorised with per-reason KPIs. A Warehouse Manager sees only their
 * warehouse(s) and quantities; Admin / Super Admin see all warehouses (with a filter) and value.
 */
export function NonSellableStockPage({ embedded }: { embedded?: boolean } = {}) {
  const role = useAuthStore((s) => s.user?.role);
  const isWHM = role === 'WAREHOUSE_MANAGER';

  const [reason, setReason] = useState('ALL');
  const [warehouseId, setWarehouseId] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (reason !== 'ALL') p.reason = reason;
    if (warehouseId !== 'ALL') p.warehouseId = warehouseId;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [reason, warehouseId, search]);

  const { data, isLoading } = useQuery({
    queryKey: ['non-sellable-stock', params],
    queryFn: () => request<any>({ url: '/stock/non-sellable', params }),
  });
  const rows: any[] = data?.rows ?? [];
  const s = data?.summary ?? { totalUnits: 0, batchCount: 0, totalValue: null, expiredUnits: 0, qcUnits: 0, blockedUnits: 0, expiredValue: null, qcValue: null, blockedValue: null };
  const hasValue = s.totalValue != null;
  const cols = hasValue ? 8 : 7; // chevron + product + batch + warehouse + expiry + qty + [value] + reason

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-all'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: !isWHM,
  });

  // A KPI shows value when the caller can see valuation, else the unit count.
  const kpiVal = (value: number | null, units: number) => (hasValue && value != null ? inr(value) : `${qf(units)} u`);

  return (
    <div>
      {!embedded && <h1 className="mb-1 text-xl font-semibold text-content">Non-sellable stock</h1>}
      <p className="mb-3 text-sm text-muted">Everything on hand that can't be sold — expired, on QC hold, blocked, or past its expiry date. Not counted in sellable stock or dispatch.</p>

      {/* KPIs by reason — click a card to filter the list to that reason (click again / Total to clear) */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi title="Total non-sellable" value={kpiVal(s.totalValue, s.totalUnits)} sub={`${s.batchCount} batch${s.batchCount === 1 ? '' : 'es'}`} accent="border-l-line" valueCls="text-content" icon={<Boxes size={15} />} active={reason === 'ALL'} onClick={() => setReason('ALL')} />
        <Kpi title="Expired" value={kpiVal(s.expiredValue, s.expiredUnits)} sub="past life / date-expired" accent="border-l-danger-500" valueCls="text-danger-600" icon={<AlertTriangle size={15} />} active={reason === 'EXPIRED'} onClick={() => setReason((r) => (r === 'EXPIRED' ? 'ALL' : 'EXPIRED'))} />
        <Kpi title="QC hold" value={kpiVal(s.qcValue, s.qcUnits)} sub="damaged / mismatch — inspect" accent="border-l-warning-500" valueCls="text-warning-600" icon={<FlaskConical size={15} />} active={reason === 'QC_HOLD'} onClick={() => setReason((r) => (r === 'QC_HOLD' ? 'ALL' : 'QC_HOLD'))} />
        <Kpi title="Blocked" value={kpiVal(s.blockedValue, s.blockedUnits)} sub="recall / regulatory hold" accent="border-l-violet-500" valueCls="text-violet-600" icon={<Ban size={15} />} active={reason === 'BLOCKED'} onClick={() => setReason((r) => (r === 'BLOCKED' ? 'ALL' : 'BLOCKED'))} />
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2.5">
        <Select
          value={reason} onChange={setReason} size="sm" className="w-44"
          options={[
            { value: 'ALL', label: 'All reasons' },
            { value: 'EXPIRED', label: 'Expired' },
            { value: 'QC_HOLD', label: 'QC hold' },
            { value: 'BLOCKED', label: 'Blocked' },
          ]}
        />
        {!isWHM && (
          <Select
            value={warehouseId} onChange={setWarehouseId} size="sm" className="w-64"
            options={[{ value: 'ALL', label: 'All warehouses' }, ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: warehouseLabel(w) }))]}
          />
        )}
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Batch / product / SKU…" size="sm" className="w-56 pl-8" />
        </div>
      </div>

      {/* Table — click a row to expand its full batch detail */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-2xs font-semibold uppercase tracking-wide text-muted">
              <th className="w-8 px-2 py-2.5"></th>
              <th className="px-3 py-2.5">Product</th>
              <th className="px-3 py-2.5">Batch</th>
              <th className="px-3 py-2.5">Warehouse</th>
              <th className="px-3 py-2.5">Expiry</th>
              <th className="px-3 py-2.5 text-right">Qty</th>
              {hasValue && <th className="px-3 py-2.5 text-right">Value</th>}
              <th className="px-3 py-2.5">Reason</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={cols} className="px-3 py-10 text-center text-muted">Loading…</td></tr>}
            {!isLoading && !rows.length && <tr><td colSpan={cols} className="px-3 py-10 text-center text-muted">No non-sellable stock 🎉</td></tr>}
            {rows.map((r) => {
              const meta = REASON_META[r.reason] ?? { label: r.reason, color: 'default' };
              const isOpen = open.has(r.batchId);
              return (
                <Fragment key={r.batchId}>
                  <tr className="cursor-pointer border-b border-line/70 hover:bg-surface-2" onClick={() => toggle(r.batchId)}>
                    <td className="px-2 py-2.5 text-muted">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-content">{r.productName || '—'}</div>
                      {r.sku && <div className="text-xs text-subtle">{r.sku}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-mono text-sm text-content">{r.batchNo}</div>
                      {r.sourceBatchNo && <div className="text-xs text-subtle">↵ from {r.sourceBatchNo}</div>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-muted">{r.warehouseCode ?? r.warehouseName ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-muted">
                      {dateOnly(r.expDate) ?? '—'}
                      {r.dateExpired && <Pill className="ml-1.5" color="red">past</Pill>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-content">{qf(r.qty)}</td>
                    {hasValue && <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-content">{inr(r.value)}</td>}
                    <td className="px-3 py-2.5"><Pill color={meta.color}>{meta.label}</Pill></td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-line bg-surface-2/40">
                      <td colSpan={cols} className="p-0">
                        <BatchDetail r={r} meta={meta} hasValue={hasValue} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {data?.truncated && (
        <div className="mt-2 text-xs text-subtle">Showing the top 500 by value — the KPIs above still cover everything. Narrow with the filters to see the rest.</div>
      )}
    </div>
  );
}

// Expanded per-batch detail: full batch facts (left) + why-it-can't-be-sold + what-to-do (right).
function BatchDetail({ r, meta, hasValue }: { r: any; meta: { label: string; color: string }; hasValue: boolean }) {
  const detail = reasonDetail(r);
  const detailRows: { label: string; value: any }[][] = [
    [{ label: 'Batch no', value: <span className="font-mono text-xs">{r.batchNo}</span> }, { label: 'Source', value: r.sourceBatchNo ? <span className="font-mono text-xs">{r.sourceBatchNo}</span> : null }],
    [{ label: 'Mfg date', value: dateOnly(r.mfgDate) }, { label: 'Exp date', value: r.expDate ? <span>{dateOnly(r.expDate)}{r.dateExpired && <Pill className="ml-1.5" color="red">past</Pill>}</span> : null }],
    [{ label: 'Warehouse', value: [r.warehouseCode, r.warehouseName].filter(Boolean).join(' · ') || null }, { label: 'On-hand', value: <span className="font-medium">{qf(r.qty)}</span> }],
    [{ label: 'Received', value: qf(r.qtyIn) }, { label: 'Issued', value: qf(r.qtyOut) }],
    ...(hasValue ? [[{ label: 'Unit cost', value: inr2(r.unitCost) }, { label: 'Locked value', value: <span className="font-medium">{inr2(r.value)}</span> }]] : []),
    [{ label: 'SKU', value: r.sku }, { label: 'Added on', value: dt(r.createdAt) }],
    [{ label: 'Last updated', value: dt(r.updatedAt) }, null as any],
  ];
  return (
    <div className="px-5 py-4">
      <div className="grid gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div>
          <ColHead>Batch details</ColHead>
          <DetailGrid rows={detailRows} />
        </div>
        <div>
          <ColHead>Why it can't be sold</ColHead>
          <div className="rounded-control border border-line bg-surface p-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <Pill color={meta.color}>{meta.label}</Pill>
              {r.status !== r.reason && <span className="text-xs text-subtle">batch status: {r.status}</span>}
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-content">{detail.what}</p>
            <div className="mt-3 flex items-start gap-2 rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">
              <Wrench size={14} className="mt-0.5 shrink-0" />
              <span>{detail.action}</span>
            </div>

            {/* Provenance — who put it in this state (return receiver) or the system flip job. */}
            {r.origin?.source === 'RETURN' && (
              <div className="mt-3 flex items-start gap-2 border-t border-line pt-2.5 text-sm text-muted">
                <UserCheck size={14} className="mt-0.5 shrink-0" />
                <span>
                  Booked in on return <span className="font-medium text-content">{r.origin.returnNo}</span>
                  {r.origin.condition && <> as {CONDITION_LABEL[r.origin.condition] ?? String(r.origin.condition).toLowerCase()}</>}
                  {r.origin.byName ? <> · flagged by <span className="font-medium text-content">{r.origin.byName}</span></> : ''}
                  {r.origin.at ? <> · {dt(r.origin.at)}</> : ''}
                </span>
              </div>
            )}
            {r.origin?.source === 'SYSTEM' && (
              <div className="mt-3 flex items-start gap-2 border-t border-line pt-2.5 text-sm text-muted">
                <Cog size={14} className="mt-0.5 shrink-0" />
                <span>Flagged automatically by the nightly expiry job{r.origin.at ? <> · {dt(r.origin.at)}</> : ''}</span>
              </div>
            )}

            {hasValue && (
              <div className="mt-3 flex items-baseline justify-between border-t border-line pt-2.5 text-sm">
                <span className="text-muted">Capital locked in this batch</span>
                <span className="font-semibold text-danger-600">{inr2(r.value)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ColHead({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-subtle">{children}</div>;
}

// Hairline key-value grid — brand-green label cells + plain value cells (matches the app's Descriptions look).
function DetailGrid({ rows }: { rows: { label: string; value: any }[][] }) {
  const labelCls = 'flex items-center bg-brand-50 px-3 py-1.5 text-xs font-medium text-muted dark:bg-brand-900/25';
  const valueCls = 'flex items-center bg-surface px-3 py-1.5 text-sm text-content';
  const fmt = (v: any) => (v == null || v === '' ? <span className="text-subtle">—</span> : v);
  return (
    <div className="overflow-hidden rounded-control border border-line">
      <div className="grid gap-px bg-line sm:grid-cols-[minmax(84px,auto)_minmax(0,1fr)_minmax(84px,auto)_minmax(0,1fr)]">
        {rows.map((pair, i) => (
          <Fragment key={i}>
            <div className={labelCls}>{pair[0].label}</div>
            <div className={valueCls}>{fmt(pair[0].value)}</div>
            {pair[1] ? (
              <>
                <div className={labelCls}>{pair[1].label}</div>
                <div className={valueCls}>{fmt(pair[1].value)}</div>
              </>
            ) : (
              <>
                <div className="hidden bg-brand-50 dark:bg-brand-900/25 sm:block" />
                <div className="hidden bg-surface sm:block" />
              </>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function Kpi({ title, value, sub, accent, valueCls, icon, active, onClick }: { title: string; value: string; sub?: string; accent: string; valueCls?: string; icon?: React.ReactNode; active?: boolean; onClick?: () => void }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`rounded-card border border-line border-l-[3px] ${accent} bg-surface px-4 py-3 text-left transition ${clickable ? 'cursor-pointer hover:bg-surface-2' : 'cursor-default'} ${active ? 'ring-2 ring-inset ring-content/20' : ''}`}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted">{icon}{title}</div>
      <div className={`mt-0.5 text-2xl font-bold tabular-nums ${valueCls ?? 'text-content'}`}>{value}</div>
      {sub && <div className="text-xs text-subtle">{sub}</div>}
    </button>
  );
}
